import { Box, Typography, Collapse, Divider, Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCalendar,
  faPills,
  faFilePrescription,
  faMagnifyingGlassLocation,
  faCapsules,
  faPrescriptionBottle
} from "@fortawesome/pro-solid-svg-icons";
import CardHeader from "./card-header";
import { useTranslation } from "react-i18next";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

const INFO_BORDER = "#8FC0FF";

function InfoBox({ icon, label, value, flex = 1 }) {
  return (
    <Box
      sx={{
        bgcolor: semanticColors.feedback.infoBg,
        border: `1px solid ${INFO_BORDER}`,
        borderRadius: "14px",
        p: "8px 16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "2px",
        flex,
        minWidth: "120px"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <FontAwesomeIcon
          icon={icon}
          style={{ color: semanticColors.status.processing, fontSize: 16 }}
        />
        <Typography
          sx={{ ...typography.body.l, color: semanticColors.text.primary }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{ ...typography.heading.m, color: semanticColors.text.primary }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SectionHeader({ title, badge, right, subtitle, linkLabel }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography
            sx={{
              ...typography.heading.m,
              fontWeight: 600,
              color: semanticColors.text.primary
            }}
          >
            {title}
          </Typography>
          {badge && (
            <Box
              sx={{
                bgcolor: semanticColors.surface.containerLow,
                borderRadius: "8px",
                px: "4px",
                py: "2px"
              }}
            >
              <Typography
                sx={{
                  ...typography.body.l,
                  color: semanticColors.text.primary
                }}
              >
                {badge}
              </Typography>
            </Box>
          )}
          {linkLabel && (
            <Typography
              sx={{
                ...typography.heading.m,
                fontWeight: 600,
                color: semanticColors.text.primary
              }}
            >
              {" "}
              •{" "}
              <Box component="span" sx={{ color: semanticColors.text.link }}>
                {linkLabel}
              </Box>
            </Typography>
          )}
        </Box>
        {right && (
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.primary }}
          >
            {right}
          </Typography>
        )}
      </Box>
      {subtitle && (
        <Typography
          sx={{ ...typography.body.l, color: semanticColors.text.primary }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export function PlanMedicationCard({
  card,
  isExpanded,
  onExpand,
  inGroup = false
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        borderRadius: "16px",
        bgcolor: "white",
        boxShadow: isExpanded
          ? "0px 0px 24px rgba(30, 39, 79, 0.16)"
          : "0px 0px 16px rgba(30, 39, 79, 0.08)",
        border: isExpanded
          ? `1px solid ${semanticColors.highlight.default}`
          : "1px solid transparent",
        overflow: "hidden",
        transition: "box-shadow 0.2s, border-color 0.2s"
      }}
    >
      <CardHeader
        card={card}
        onExpand={onExpand}
        isExpanded={isExpanded}
        inGroup={inGroup}
      />

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: { xs: "16px", md: "32px" },
            pb: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "24px"
          }}
        >
          <Divider sx={{ borderColor: semanticColors.border.middle }} />

          {/* Section 1: Tratamento */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <SectionHeader
              title={t("planMedication.treatment")}
              badge={card.route}
              right={
                card.startDate || card.endDate
                  ? `${
                      card.startDate
                        ? `${t("planMedication.startDate")}: ${card.startDate}`
                        : ""
                    }${card.startDate && card.endDate ? "    " : ""}${
                      card.endDate
                        ? `${t("planMedication.endDate")}: ${card.endDate}`
                        : ""
                    }`
                  : undefined
              }
              subtitle={t("planMedication.howToTake")}
            />

            <Box
              sx={{
                display: "flex",
                gap: "16px",
                justifyContent: "space-between",
                alignItems: "flex-end",
                flexWrap: { xs: "wrap", md: "nowrap" }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: 0
                }}
              >
                {card.doses?.map((dose, i) => (
                  <Box key={i} sx={{ display: "flex", gap: "16px" }}>
                    <InfoBox
                      icon={faPills}
                      label={t("planMedication.dosePerTake")}
                      value={dose.dose}
                      flex="none"
                    />
                    <InfoBox
                      icon={faClock}
                      label={t("planMedication.frequency")}
                      value={dose.frequency}
                      flex={1}
                    />
                  </Box>
                ))}
              </Box>
              {card.treatmentDuration && (
                <InfoBox
                  icon={faCalendar}
                  label={t("planMedication.duration")}
                  value={card.treatmentDuration}
                  flex="none"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: semanticColors.border.middle }} />

          {/* Section 2: Stock */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <SectionHeader
              title={t("planMedication.stockSection")}
              right={
                card.stockBreakDate
                  ? `${t("planMedication.stockBreak")}: ${card.stockBreakDate}`
                  : undefined
              }
              subtitle={card.packageContent}
            />
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                justifyContent: "space-between",
                alignItems: "flex-end"
              }}
            >
              {card.stockCount !== undefined && (
                <InfoBox
                  icon={faCapsules}
                  label={t("planMedication.availability")}
                  value={`${card.stockCount} tomas`}
                  flex="none"
                />
              )}
              {card.stockDuration && (
                <InfoBox
                  icon={faCalendar}
                  label={t("planMedication.duration")}
                  value={card.stockDuration}
                  flex="none"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: semanticColors.border.middle }} />

          {/* Section 3: Receita médica */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <SectionHeader
              title={t("planMedication.prescriptionSection")}
              linkLabel={
                card.prescription?.guideLink
                  ? t("planMedication.treatmentGuide")
                  : undefined
              }
              right={
                card.prescription?.validUntil
                  ? `${t("planMedication.validUntil")}: ${
                      card.prescription.validUntil
                    }`
                  : undefined
              }
              subtitle={
                card.prescription?.totalPackages
                  ? `${t("planMedication.prescriptionContains")} ${
                      card.prescription.totalPackages
                    } ${t("planMedication.packages")}`
                  : undefined
              }
            />
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                justifyContent: "space-between",
                alignItems: "flex-end"
              }}
            >
              <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {card.prescription?.status && (
                  <InfoBox
                    icon={faFilePrescription}
                    label={t("planMedication.prescriptionStatus")}
                    value={card.prescription.status}
                    flex="none"
                  />
                )}
                {card.prescription?.toDispense && (
                  <InfoBox
                    icon={faPrescriptionBottle}
                    label={t("planMedication.toDispense")}
                    value={card.prescription.toDispense}
                    flex="none"
                  />
                )}
              </Box>
              {card.prescription?.duration && (
                <InfoBox
                  icon={faCalendar}
                  label={t("planMedication.duration")}
                  value={card.prescription.duration}
                  flex="none"
                />
              )}
            </Box>
          </Box>

          {/* Action buttons */}
          <Box
            sx={{ display: "flex", gap: "16px", flexWrap: "wrap", pt: "8px" }}
          >
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faFilePrescription} />}
              sx={{
                ...typography.body.lSemibold,
                flex: 1,
                py: "14px",
                borderRadius: "100px",
                color: semanticColors.highlight.default,
                border: `2px solid ${semanticColors.highlight.default}`,
                "&:hover": {
                  border: `2px solid ${semanticColors.highlight.strong}`,
                  bgcolor: semanticColors.highlight.low
                }
              }}
            >
              {t("planMedication.renewPrescription")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faMagnifyingGlassLocation} />}
              sx={{
                ...typography.body.lSemibold,
                flex: 1,
                py: "14px",
                borderRadius: "100px",
                color: semanticColors.highlight.default,
                border: `2px solid ${semanticColors.highlight.default}`,
                "&:hover": {
                  border: `2px solid ${semanticColors.highlight.strong}`,
                  bgcolor: semanticColors.highlight.low
                }
              }}
            >
              {t("planMedication.findPharmacies")}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
